import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Upload, Info, History, Trophy, Medal, Users, Shirt, Image as ImageIcon, PanelBottom, LayoutGrid, RotateCcw } from "lucide-react";

const field = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";

function Cell({ col, defaultValue }) {
  const val = col.type === "lines" ? (Array.isArray(defaultValue) ? defaultValue.join("\n") : "") : (defaultValue ?? "");
  if (col.type === "textarea" || col.type === "lines")
    return <textarea name={col.name} defaultValue={val} rows={col.type === "lines" ? 3 : 2} placeholder={col.label} className={`resize-none ${field}`} />;
  if (col.type === "select")
    return <select name={col.name} defaultValue={val} className={`bg-white ${field}`}>{col.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  return <input name={col.name} defaultValue={val} placeholder={col.label} className={field} />;
}

function collect(form, columns) {
  const fd = new FormData(form);
  const body = {};
  columns.forEach((c) => {
    const v = fd.get(c.name);
    body[c.name] = c.type === "lines" ? String(v || "").split("\n").map((x) => x.trim()).filter(Boolean) : (v || "");
  });
  return body;
}

function ListEditor({ section, columns, image, addLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/content/${section}`);
    setItems(data); setLoading(false);
  }, [section]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/content/${section}`, collect(e.target, columns)); e.target.reset(); toast.success("Agregado"); load(); }
    catch { toast.error("No se pudo agregar"); }
  };
  const save = async (e, id) => {
    e.preventDefault(); setBusy(id);
    try { await api.patch(`/content/${section}/${id}`, collect(e.target, columns)); toast.success("Guardado"); load(); }
    catch { toast.error("No se pudo guardar"); } finally { setBusy(null); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/content/${section}/${id}`); toast.success("Eliminado"); load();
  };
  const upload = async (file, id) => {
    if (!file) return; setBusy(id);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/content/${section}/${id}/image`, fd); setItems((xs) => xs.map((x) => x.id === id ? { ...x, image_url: data.image_url } : x)); toast.success("Imagen subida"); }
    catch { toast.error("No se pudo subir"); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={add} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center gap-2 text-navy font-bold"><Plus className="h-4 w-4 text-gold" /> {addLabel}</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {columns.map((c) => <div key={c.name} className={c.type === "textarea" || c.type === "lines" ? "sm:col-span-2" : ""}><Cell col={c} /></div>)}
        </div>
        <button data-testid={`content-add-${section}`} className="mt-3 inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#16235a]"><Plus className="h-4 w-4" /> Agregar</button>
      </form>

      {loading ? (
        <div className="py-10 flex justify-center text-navy/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <form key={it.id} onSubmit={(e) => save(e, it.id)} data-testid={`content-item-${section}-${it.id}`} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <div className="flex gap-4">
                {image && (
                  <div className="w-24 shrink-0">
                    <div className="aspect-square rounded-lg overflow-hidden bg-[#eef1f7] flex items-center justify-center">
                      {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-6 w-6 text-navy/30" />}
                    </div>
                    <label className="mt-1.5 flex items-center justify-center gap-1 text-[11px] font-semibold text-navy border border-gray-200 rounded-lg py-1 cursor-pointer hover:border-navy/40">
                      {busy === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Foto
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], it.id)} />
                    </label>
                  </div>
                )}
                <div className="flex-1 grid sm:grid-cols-2 gap-3">
                  {columns.map((c) => <div key={c.name} className={c.type === "textarea" || c.type === "lines" ? "sm:col-span-2" : ""}><Cell col={c} defaultValue={it[c.name]} /></div>)}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button data-testid={`content-save-${it.id}`} disabled={busy === it.id} className="inline-flex items-center gap-1.5 bg-navy text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Guardar</button>
                <button type="button" onClick={() => del(it.id)} data-testid={`content-del-${it.id}`} className="inline-flex items-center gap-1.5 text-[#c0392b] border border-[#c0392b]/30 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#c0392b]/5"><Trash2 className="h-3.5 w-3.5" /> Eliminar</button>
              </div>
            </form>
          ))}
          {items.length === 0 && <p className="text-sm text-gray-400">Sin elementos todavía.</p>}
        </div>
      )}
    </div>
  );
}

function NosotrosEditor() {
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/content/nosotros");
    setData(data); setBlocks(data.blocks || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const fd = new FormData(e.target);
    try {
      const { data: res } = await api.put("/content/nosotros", { lead: fd.get("lead"), blocks });
      setData(res); toast.success("El Club actualizado");
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };
  const upload = async (file) => {
    if (!file) return; setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data: res } = await api.post("/content/nosotros/image", fd); setData((d) => ({ ...d, image_url: res.image_url })); toast.success("Imagen subida"); }
    catch { toast.error("No se pudo subir"); } finally { setUploading(false); }
  };
  const setBlock = (i, k, v) => setBlocks((bs) => bs.map((b, idx) => idx === i ? { ...b, [k]: v } : b));

  if (!data) return <div className="py-10 flex justify-center text-navy/40"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
      <div className="flex items-center gap-2 text-navy font-bold"><Info className="h-4 w-4 text-gold" /> El Club (introducción)</div>
      <div className="flex gap-4 items-start">
        <div className="w-32 shrink-0">
          <div className="aspect-video rounded-lg overflow-hidden bg-[#eef1f7] flex items-center justify-center">
            {data.image_url ? <img src={data.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-6 w-6 text-navy/30" />}
          </div>
          <label className="mt-1.5 flex items-center justify-center gap-1 text-[11px] font-semibold text-navy border border-gray-200 rounded-lg py-1 cursor-pointer hover:border-navy/40">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Imagen
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
        <textarea name="lead" defaultValue={data.lead} rows={4} placeholder="Texto de introducción" className={`flex-1 resize-none ${field}`} data-testid="nosotros-lead" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-navy">Bloques ({blocks.length})</span>
          <button type="button" onClick={() => setBlocks((bs) => [...bs, { icon: "history", title: "", text: "" }])} className="text-xs font-semibold text-navy hover:text-gold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Bloque</button>
        </div>
        <div className="mt-3 space-y-3">
          {blocks.map((b, i) => (
            <div key={i} className="grid sm:grid-cols-12 gap-2 items-start bg-[#eef1f7] rounded-lg p-3">
              <select value={b.icon} onChange={(e) => setBlock(i, "icon", e.target.value)} className={`sm:col-span-2 bg-white ${field}`}>
                {["history", "field", "fans", "infra"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <input value={b.title} onChange={(e) => setBlock(i, "title", e.target.value)} placeholder="Título" className={`sm:col-span-3 ${field}`} />
              <input value={b.text} onChange={(e) => setBlock(i, "text", e.target.value)} placeholder="Texto" className={`sm:col-span-6 ${field}`} />
              <button type="button" onClick={() => setBlocks((bs) => bs.filter((_, idx) => idx !== i))} className="sm:col-span-1 h-9 w-9 rounded-lg bg-white text-[#c0392b] flex items-center justify-center hover:bg-[#c0392b]/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="nosotros-save" disabled={saving} className="inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar El Club
      </button>
    </form>
  );
}

function HeroEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/content/hero");
    setItems(data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addSlide = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { data: item } = await api.post("/content/hero", {});
      await api.post(`/content/hero/${item.id}/image`, (() => { const f = new FormData(); f.append("file", file); return f; })());
      toast.success("Imagen agregada al slider");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Quitar esta imagen del slider?")) return;
    await api.delete(`/content/hero/${id}`);
    toast.success("Imagen quitada"); load();
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 text-navy font-bold"><ImageIcon className="h-4 w-4 text-gold" /> Imágenes del slider (Hero)</div>
      <p className="mt-1 text-sm text-gray-500">Estas imágenes rotan en la portada del sitio. Subí en formato horizontal para mejor resultado.</p>
      {loading ? (
        <div className="py-10 flex justify-center text-navy/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`hero-slide-${s.id}`} className="group relative aspect-video rounded-xl overflow-hidden border border-gray-100 bg-[#eef1f7]">
              {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-navy/30"><ImageIcon className="h-6 w-6" /></div>}
              <button onClick={() => del(s.id)} data-testid={`hero-del-${s.id}`} className="absolute top-2 right-2 h-8 w-8 rounded-lg bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#c0392b]">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <label className="aspect-video rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:border-navy/40">
            {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Upload className="h-6 w-6" /><span className="mt-1 text-xs font-semibold">Agregar imagen</span></>}
            <input type="file" accept="image/*" className="hidden" data-testid="hero-add-input" onChange={(e) => addSlide(e.target.files?.[0])} disabled={uploading} />
          </label>
        </div>
      )}
    </div>
  );
}

const FOOTER_FIELDS = [
  { name: "description", label: "Descripción breve del club", type: "textarea" },
  { name: "address", label: "Dirección", type: "text" },
  { name: "phone_display", label: "Teléfono (visible)", type: "text" },
  { name: "phone", label: "Teléfono (para llamar, ej: +5491122334455)", type: "text" },
  { name: "email", label: "Email de contacto", type: "text" },
  { name: "hours", label: "Horario de sede", type: "text" },
  { name: "instagram", label: "URL de Instagram", type: "text" },
  { name: "facebook", label: "URL de Facebook", type: "text" },
  { name: "youtube", label: "URL de YouTube", type: "text" },
];

function FooterEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/content/footer");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const fd = new FormData(e.target);
    const body = {};
    FOOTER_FIELDS.forEach((f) => { body[f.name] = fd.get(f.name) || ""; });
    try {
      const { data: res } = await api.put("/content/footer", body);
      setData(res); toast.success("Pie de página actualizado");
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  if (!data) return <div className="py-10 flex justify-center text-navy/40"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 text-navy font-bold"><PanelBottom className="h-4 w-4 text-gold" /> Datos del pie de página</div>
      <p className="mt-1 text-sm text-gray-500">Contacto, horarios y redes sociales que se muestran en el footer de todo el sitio.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {FOOTER_FIELDS.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="block text-xs font-semibold text-navy mb-1">{f.label}</label>
            {f.type === "textarea"
              ? <textarea name={f.name} data-testid={`footer-field-${f.name}`} defaultValue={data[f.name] || ""} rows={2} className={`resize-none ${field}`} />
              : <input name={f.name} data-testid={`footer-field-${f.name}`} defaultValue={data[f.name] || ""} className={field} />}
          </div>
        ))}
      </div>
      <button data-testid="footer-save" disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar pie de página
      </button>
    </form>
  );
}

const EXPLORA_LABELS = {
  historia: "Historia", galeria: "Galería", palmares: "Palmarés", equipos: "Equipos", comision: "Comisión Directiva",
};
const EXPLORA_AUTO_HINT = {
  historia: "Imagen de la sección El Club", galeria: "Portada del álbum más reciente",
  palmares: "Portada de un álbum del club", equipos: "Foto de un jugador del plantel", comision: "Foto de un integrante",
};

function ExploraEditor() {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/content/explora");
    setTiles(data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file, tile) => {
    if (!file) return; setBusy(tile);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/content/explora/${tile}/image`, fd); setTiles((xs) => xs.map((x) => x.id === tile ? data : x)); toast.success("Imagen actualizada"); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); } finally { setBusy(null); }
  };
  const reset = async (tile) => {
    setBusy(tile);
    try { const { data } = await api.delete(`/content/explora/${tile}/image`); setTiles((xs) => xs.map((x) => x.id === tile ? data : x)); toast.success("Volvió a la imagen automática"); }
    catch { toast.error("No se pudo restablecer"); } finally { setBusy(null); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 text-navy font-bold"><LayoutGrid className="h-4 w-4 text-gold" /> Sección "Todo el Comandante, a un clic"</div>
      <p className="mt-1 text-sm text-gray-500">Cada tarjeta usa <b>automáticamente</b> una imagen real del sitio. Podés subir una imagen propia para reemplazarla, o volver a la automática cuando quieras.</p>
      {loading ? (
        <div className="py-10 flex justify-center text-navy/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t) => (
            <div key={t.id} data-testid={`explora-tile-${t.id}`} className="rounded-xl border border-gray-100 overflow-hidden">
              <div className="relative aspect-video bg-navy">
                {t.image_url
                  ? <img src={t.image_url} alt={EXPLORA_LABELS[t.id]} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="h-7 w-7" /></div>}
                <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${t.source === "override" ? "bg-gold text-navy" : "bg-white/85 text-navy"}`}>
                  {t.source === "override" ? "Personalizada" : "Automática"}
                </span>
              </div>
              <div className="p-3">
                <div className="font-semibold text-navy text-sm">{EXPLORA_LABELS[t.id]}</div>
                <div className="text-[11px] text-gray-400">{EXPLORA_AUTO_HINT[t.id]}</div>
                <div className="mt-2 flex items-center gap-2">
                  <label className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-navy border border-gray-200 rounded-lg py-1.5 cursor-pointer hover:border-navy/40">
                    {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Subir
                    <input type="file" accept="image/*" className="hidden" data-testid={`explora-upload-${t.id}`} onChange={(e) => upload(e.target.files?.[0], t.id)} disabled={busy === t.id} />
                  </label>
                  {t.source === "override" && (
                    <button type="button" onClick={() => reset(t.id)} data-testid={`explora-reset-${t.id}`} title="Usar imagen automática" className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-navy/40">
                      <RotateCcw className="h-3.5 w-3.5" /> Auto
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SUBTABS = [
  { id: "nosotros", label: "El Club", icon: Info },
  { id: "hero", label: "Hero (slider)", icon: ImageIcon },
  { id: "explora", label: "Explorá el club", icon: LayoutGrid },
  { id: "historia", label: "Historia", icon: History },
  { id: "palmares", label: "Palmarés", icon: Trophy },
  { id: "honors", label: "Logros", icon: Medal },
  { id: "comision", label: "Comisión", icon: Users },
  { id: "equipos", label: "Equipos", icon: Shirt },
  { id: "footer", label: "Pie de página", icon: PanelBottom },
];

const CONFIG = {
  historia: { section: "timeline", addLabel: "Nuevo hito", image: false, columns: [
    { name: "year", label: "Año", type: "text" }, { name: "title", label: "Título", type: "text" }, { name: "text", label: "Descripción", type: "textarea" },
  ] },
  palmares: { section: "palmares", addLabel: "Nuevo contador", image: false, columns: [
    { name: "count", label: "Número", type: "text" }, { name: "label", label: "Etiqueta", type: "text" },
  ] },
  honors: { section: "honors", addLabel: "Nuevo logro", image: false, columns: [
    { name: "year", label: "Año", type: "text" }, { name: "title", label: "Título", type: "text" }, { name: "tier", label: "Nivel", type: "select", options: ["oro", "plata", "bronce"] },
  ] },
  comision: { section: "comision", addLabel: "Nuevo integrante", image: true, columns: [
    { name: "group", label: "Grupo", type: "text" }, { name: "name", label: "Nombre", type: "text" }, { name: "role", label: "Rol", type: "text" }, { name: "desc", label: "Descripción", type: "textarea" },
  ] },
  equipos: { section: "equipos", addLabel: "Nueva categoría", image: true, columns: [
    { name: "name", label: "Nombre", type: "text" }, { name: "age", label: "Edades", type: "text" }, { name: "desc", label: "Descripción", type: "textarea" }, { name: "points", label: "Puntos (uno por línea)", type: "lines" },
  ] },
};

export default function AdminContentTab() {
  const [sub, setSub] = useState("nosotros");
  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap gap-2 mb-6">
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            data-testid={`content-subtab-${t.id}`}
            onClick={() => setSub(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${sub === t.id ? "bg-navy text-white" : "bg-white text-navy border border-gray-200 hover:border-navy/40"}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {sub === "nosotros" ? <NosotrosEditor /> : sub === "hero" ? <HeroEditor /> : sub === "footer" ? <FooterEditor /> : sub === "explora" ? <ExploraEditor /> : <ListEditor key={sub} {...CONFIG[sub]} />}
    </main>
  );
}
